import React from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import type { BookingsModuleConfig } from '@/types/bookings'
import type { ModuleFieldComponentProps } from '@/types/space-wizard'
import { FormField } from './FormField'
import { WeeklyHoursEditor } from './WeeklyHoursEditor'
import { type BookingsConfigErrors, doorPanelOf, parseDurations } from './bookingsConfig'
import type { DoorPanelConfig } from '@/types/bookings'

interface Props extends ModuleFieldComponentProps<BookingsModuleConfig> {
  errors?: BookingsConfigErrors | null
}

const toNumber = (raw: string): number => (raw === '' ? Number.NaN : Number(raw))

/**
 * The Reservas setup (PROPOSTA-COWORKING-RESERVAS section 7.5): the space-wide defaults every
 * resource inherits (slot, duration limits, notice, horizon, weekly hours) and whether the agenda
 * shows who booked. The wizard asks it as a step of its own (PADROES-UI section 14) and the space
 * edit shows the very same component.
 */
export const BookingsSpaceFields = ({ config, onChange, disabled = false, errors }: Props) => {
  const { t } = useTranslation('bookings')
  const { t: tp } = useTranslation('roomPanel')
  const number = (key: keyof Pick<BookingsModuleConfig, 'slotMinutes' | 'minDurationMinutes' | 'maxDurationMinutes' | 'minNoticeMinutes' | 'maxAdvanceDays'>, label: string, hint: string, min: number) => (
    <FormField label={label} htmlFor={`bk-${key}`} error={errors?.[key]} hint={hint}>
      <Input id={`bk-${key}`} type="number" inputMode="numeric" min={min} value={Number.isNaN(config[key]) ? '' : config[key]} disabled={disabled}
        onChange={(e) => onChange({ ...config, [key]: toNumber(e.target.value) })} aria-invalid={Boolean(errors?.[key])} className="max-w-[12rem]" />
    </FormField>
  )

  const panel = doorPanelOf(config)
  const setPanel = (patch: Partial<DoorPanelConfig>) => onChange({ ...config, doorPanel: { ...panel, ...patch } })
  /* The durations are typed as text ("15, 30, 60") and kept as text while the person edits. */
  const [durations, setDurations] = React.useState(() => panel.quickDurations.join(', '))
  const toggle = (key: 'releaseOnNoCheckIn' | 'showStartButton' | 'showBookerFirstName' | 'showTitle', label: string, hint: string, locked = false) => (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{hint}</p>
      </div>
      <Switch checked={panel[key] || locked} disabled={disabled || locked} aria-label={label} onCheckedChange={(on) => setPanel({ [key]: on })} />
    </div>
  )

  return (
    <div className="space-y-6" data-testid="bookings-setup">
      <div className="grid gap-4 sm:grid-cols-2">
        {number('slotMinutes', t('config.slotMinutes'), t('config.slotHint'), 5)}
        {number('minDurationMinutes', t('config.minDuration'), t('config.minDurationHint'), 5)}
        {number('maxDurationMinutes', t('config.maxDuration'), t('config.maxDurationHint'), 5)}
        {number('minNoticeMinutes', t('config.minNotice'), t('config.minNoticeHint'), 0)}
        {number('maxAdvanceDays', t('config.maxAdvance'), t('config.maxAdvanceHint'), 1)}
      </div>

      <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">{t('config.showBookerName')}</p>
          <p className="text-xs text-gray-500">{t('config.showBookerNameHint')}</p>
        </div>
        <Switch checked={config.showBookerName} disabled={disabled} aria-label={t('config.showBookerName')}
          onCheckedChange={(on) => onChange({ ...config, showBookerName: on })} />
      </div>

      <div className="space-y-2">
        <div>
          <p className="text-sm font-medium text-gray-700">{t('config.hours')}</p>
          <p className="text-xs text-gray-500">{t('config.hoursHint')}</p>
        </div>
        <WeeklyHoursEditor value={config.openingHours} onChange={(openingHours) => onChange({ ...config, openingHours })} disabled={disabled} />
        {errors?.openingHours && <p role="alert" className="text-xs text-red-600">{errors.openingHours}</p>}
      </div>

      <div className="space-y-3" data-testid="door-panel-settings">
        <div>
          <p className="text-sm font-medium text-gray-700">{tp('config.title')}</p>
          <p className="text-xs text-gray-500">{tp('config.hint')}</p>
        </div>
        {toggle('releaseOnNoCheckIn', tp('config.release'), tp('config.releaseHint', { minutes: panel.graceMinutes }))}
        {panel.releaseOnNoCheckIn && (
          <FormField label={tp('config.grace')} htmlFor="bk-grace" hint={tp('config.graceHint')}>
            <Input id="bk-grace" type="number" inputMode="numeric" min={1} max={120} value={Number.isNaN(panel.graceMinutes) ? '' : panel.graceMinutes} disabled={disabled}
              onChange={(e) => setPanel({ graceMinutes: toNumber(e.target.value) })} className="max-w-[12rem]" />
          </FormField>
        )}
        {toggle('showStartButton', tp('config.startButton'), tp('config.startButtonHint'), panel.releaseOnNoCheckIn)}
        <FormField label={tp('config.durations')} htmlFor="bk-durations" hint={tp('config.durationsHint')}>
          <Input id="bk-durations" value={durations} disabled={disabled} className="max-w-[16rem]"
            onChange={(e) => { setDurations(e.target.value); const list = parseDurations(e.target.value); setPanel({ quickDurations: list ?? [] }) }} />
        </FormField>
        {toggle('showBookerFirstName', tp('config.firstName'), tp('config.firstNameHint'))}
        {toggle('showTitle', tp('config.showTitle'), tp('config.showTitleHint'))}
        {errors?.doorPanel && <p role="alert" className="text-xs text-red-600">{errors.doorPanel}</p>}
      </div>

    </div>
  )
}
